/**
 * @file UnlockPasswordSettings.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-09-30
 * @description Transport Settings → Unlock Password: the shared password that
 *              unlocks a locked transport day — any day, and a past one for
 *              half an hour to correct haulier, driver and vehicle.
 *
 *              Shown to admins and holders of transport.unlock_password.view
 *              (the tab is hidden from everyone else, and the server refuses
 *              them). Only an admin can change it. Server:
 *              services/pastDayUnlockService.js.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './UnlockPasswordSettings.css';
import Input from './Input';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';

function ResetBody({ onDone, onClose }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useSlideOutButton([saving]);

  const save = async (generate) => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/transport/unlock-password`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generate ? { generate: true } : { password: value })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not change the password');
      onDone();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <>
      <div>
        <p>Type a new unlock password (at least 6 characters), or generate one. The old one stops working at once.</p>
        <Input type="text" value={value} onChange={(e) => setValue(e.target.value)} placeholder="New unlock password" autoFocus />
        {error && <p className="upw-error">{error}</p>}
      </div>
      <div className="upw-actions">
        <div className="buttons slideout">
          <div><button className="btn-slideout" onClick={onClose} disabled={saving}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
            <span className="btn-label">Cancel</span>
          </button></div>
          <div><button className="btn-slideout" onClick={() => save(true)} disabled={saving}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">casino</span></div>
            <span className="btn-label">Generate</span>
          </button></div>
          <div><button className="btn-slideout" onClick={() => save(false)} disabled={saving || value.trim().length < 6}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
            <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
          </button></div>
        </div>
      </div>
    </>
  );
}

function UnlockPasswordSettings() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);
  useSlideOutButton([state, shown]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/transport/unlock-password`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load the unlock password');
      setState(d);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard refused — it is on screen */ }
  };

  const setAt = state?.setAt
    ? new Date(state.setAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="search-layout unlock-password-settings">
      <div className="search-header">
        <div className="workflow-info">
          <span className="info">Unlock Password</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              Unlocks a locked transport day. On a day that has passed it opens for {state?.unlockMinutes || 30} minutes,
              to correct the haulier, driver and vehicle only. Anyone who has it — and can edit transport planning — can unlock.
            </span>
          </span>
        </div>
      </div>

      <div className="upw-panel">
        {error && <p className="upw-error">{error}</p>}
        {!state && !error && <LoadingSpinner />}
        {state && (
          <>
            {!state.set && <p className="upw-notice">No unlock password has been set, so locked days cannot be unlocked. {state.canReset ? 'Set one below.' : 'Ask an admin to set one.'}</p>}
            {state.unreadable && <p className="upw-notice">The stored password can no longer be read (the server's key changed). {state.canReset ? 'Reset it below.' : 'Ask an admin to reset it.'}</p>}
            {state.set && !state.unreadable && (
              <div className="upw-value-row">
                <code className="upw-value">{shown ? state.password : '•'.repeat(Math.max(state.password.length, 8))}</code>
                <button type="button" className="btn-icon" title={shown ? 'Hide' : 'Show'} onClick={() => setShown(v => !v)}>
                  <span className="material-symbols-rounded">{shown ? 'visibility_off' : 'visibility'}</span>
                </button>
                <button type="button" className="btn-icon" title={copied ? 'Copied' : 'Copy'} onClick={copy}>
                  <span className="material-symbols-rounded">{copied ? 'check' : 'content_copy'}</span>
                </button>
              </div>
            )}
            {setAt && <p className="upw-meta">Last changed {setAt}{state.setBy ? ` by ${state.setBy}` : ''}.</p>}
            <div className="buttons slideout">
              <div>
                <button
                  className={`btn-slideout${state.canReset ? '' : ' btn-disabled'}`}
                  disabled={!state.canReset}
                  title={state.canReset ? 'Change the unlock password' : 'Only an admin can change the unlock password'}
                  onClick={() => setResetting(true)}
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">key</span></div>
                  <span className="btn-label">{state.set ? 'Change Password' : 'Set Password'}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal isOpen={resetting} onClose={() => setResetting(false)} title="Change Unlock Password" size="sm" className="unlock-password-reset">
        {resetting && <ResetBody onClose={() => setResetting(false)} onDone={() => { setResetting(false); setShown(true); load(); }} />}
      </Modal>
    </div>
  );
}

export default UnlockPasswordSettings;
